import React, { useState, useRef } from 'react';
import { API } from '@/App';
import { X, Download, Grid3X3, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export function FieldLayoutModal({ analysisId, designLabel, designColor, onClose }) {
  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trialName, setTrialName] = useState('');
  const [crop, setCrop] = useState('');
  const [exporting, setExporting] = useState(false);
  const gridRef = useRef(null);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/field-layout`, {
        analysis_id: analysisId,
        trial_name: trialName,
        crop,
      }, { withCredentials: true });
      setLayout(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Layout generation failed');
    }
    setLoading(false);
  };

  const exportPDF = async () => {
    if (!gridRef.current) return;
    setExporting(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');
      const canvas = await html2canvas(gridRef.current, {
        scale: 2, backgroundColor: '#ffffff', useCORS: true,
      });
      const imgData = canvas.toDataURL('image/png');
      const imgW = canvas.width;
      const imgH = canvas.height;
      const pdfW = 297; // A4 landscape width in mm
      const pdfH = 210;
      const ratio = Math.min((pdfW - 20) / imgW, (pdfH - 40) / imgH);
      const pdf = new jsPDF({ orientation: imgW > imgH ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const x = (pageW - imgW * ratio) / 2;
      pdf.addImage(imgData, 'PNG', x, 10, imgW * ratio, imgH * ratio);
      pdf.save(`FieldLayout_${layout?.design_type || 'design'}.pdf`);
      toast.success('PDF downloaded!');
    } catch {
      toast.error('PDF export failed');
    }
    setExporting(false);
  };

  // Auto-scale cell size
  const getCellSize = () => {
    if (!layout) return 64;
    const totalCells = (layout.n_cols_with_border || layout.n_cols) * (layout.n_rows_with_border || layout.n_rows);
    if (totalCells <= 30) return 72;
    if (totalCells <= 60) return 60;
    if (totalCells <= 120) return 48;
    return 38;
  };

  const cellSize = getCellSize();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="field-layout-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-[95vw] max-w-[1100px] max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8E6E1]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: designColor || '#2D8B57' }}>
              <Grid3X3 size={18} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#2C2A28]">Field Layout Generator</h2>
              <p className="text-xs text-[#8A8580]">{designLabel} — Randomized plot map</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#f5f5f5] transition" data-testid="close-layout-modal">
            <X size={18} className="text-[#888]" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-6 py-5">
          {!layout ? (
            <div className="space-y-4">
              <p className="text-sm text-[#5E5A56]">Generate a randomized field plot layout from your analysis data. Optionally add trial info for the PDF header.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-[#2C2A28] mb-1 block">Trial Name (optional)</label>
                  <input type="text" value={trialName} onChange={e => setTrialName(e.target.value)}
                    placeholder="e.g. Wheat Variety Trial 2025"
                    className="w-full px-3 py-2 text-sm border border-[#E8E6E1] rounded-lg focus:outline-none focus:border-[#2D8B57]"
                    data-testid="trial-name-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#2C2A28] mb-1 block">Crop (optional)</label>
                  <input type="text" value={crop} onChange={e => setCrop(e.target.value)}
                    placeholder="e.g. Wheat, Rice, Maize"
                    className="w-full px-3 py-2 text-sm border border-[#E8E6E1] rounded-lg focus:outline-none focus:border-[#2D8B57]"
                    data-testid="crop-input" />
                </div>
              </div>
              <button onClick={generate} disabled={loading}
                className="w-full py-3 rounded-xl font-semibold text-white text-sm transition-all disabled:opacity-50"
                style={{ backgroundColor: designColor || '#2D8B57' }}
                data-testid="generate-layout-btn">
                {loading ? (
                  <span className="flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" /> Generating...</span>
                ) : 'Generate Randomized Layout'}
              </button>
            </div>
          ) : (
            <div>
              {/* Printable area */}
              <div ref={gridRef} className="bg-white p-6">
                {/* PDF Header */}
                <div className="mb-5 text-center">
                  <h3 className="text-base font-bold text-[#1a472a]">{layout.trial_name || 'Field Layout'}</h3>
                  <div className="flex items-center justify-center gap-4 mt-1 text-xs text-[#5E5A56]">
                    <span>{layout.analysis_type_label}</span>
                    {layout.crop && <span>Crop: {layout.crop}</span>}
                    <span>{layout.treatments?.length} treatments x {layout.reps} reps</span>
                    <span>Generated: {new Date().toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Grid */}
                <div className="overflow-x-auto">
                  <div className="inline-block">
                    {/* Column headers */}
                    <div className="flex" style={{ paddingLeft: 64 }}>
                      {layout.grid[0]?.map((_, ci) => (
                        <div key={ci} style={{ width: cellSize, minWidth: cellSize }}
                          className="text-center text-[9px] font-medium text-[#8A8580] pb-1 truncate">
                          {layout.has_border ? (ci === 0 || ci === layout.grid[0].length - 1 ? '' : layout.col_labels?.[ci - 1] || '') : (layout.col_labels?.[ci] || '')}
                        </div>
                      ))}
                    </div>

                    {layout.grid.map((row, ri) => (
                      <div key={ri} className="flex items-center">
                        {/* Row label */}
                        <div className="text-[9px] font-medium text-[#8A8580] w-[64px] text-right pr-2 truncate shrink-0">
                          {layout.has_border ? (ri === 0 || ri === layout.grid.length - 1 ? '' : layout.row_labels?.[ri - 1] || '') : (layout.row_labels?.[ri] || '')}
                        </div>
                        {row.map((cell, ci) => {
                          const isBorder = cell.type === 'border';
                          const isEmpty = cell.type === 'empty' || (!cell.treatment && !cell.plot);
                          const bgColor = isBorder ? '#D5D1CC' : (layout.color_map?.[cell.treatment] || '#F9F8F6');
                          const textColor = isBorder ? '#777' : '#1a1a1a';
                          return (
                            <div key={ci}
                              style={{
                                width: cellSize, minWidth: cellSize, height: cellSize,
                                backgroundColor: isEmpty ? 'transparent' : (isBorder ? undefined : bgColor + '30'),
                                borderColor: isBorder ? '#1a472a' : '#1a472a50',
                              }}
                              className={`border flex flex-col items-center justify-center text-center relative
                                ${isBorder ? 'bg-[#D5D1CC]' : ''}`}
                              data-testid={isBorder ? 'border-cell' : `plot-cell-${cell.plot}`}
                            >
                              {isBorder ? (
                                <>
                                  <div className="absolute inset-0 opacity-20" style={{
                                    background: 'repeating-linear-gradient(45deg, #888 0, #888 1px, transparent 0, transparent 6px)',
                                  }} />
                                  <span className="text-[8px] font-bold text-[#666] relative z-10">BORDER</span>
                                </>
                              ) : isEmpty ? null : (
                                <>
                                  <span className="text-[10px] font-bold" style={{ color: textColor }}>{cell.plot}</span>
                                  <span className="text-[7px] font-medium leading-tight truncate w-full px-0.5"
                                    style={{ color: layout.color_map?.[cell.treatment] || '#2D8B57' }}>
                                    {cell.treatment}
                                  </span>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Legend */}
                <div className="mt-5 pt-4 border-t border-[#E8E6E1]">
                  <p className="text-[10px] font-bold text-[#2C2A28] mb-2">Treatment Legend</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                    {layout.treatments?.map((t, i) => (
                      <div key={t} className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded-sm border border-[#1a472a]"
                          style={{ backgroundColor: layout.color_map?.[t] || '#2D8B57' }} />
                        <span className="text-[10px] text-[#5E5A56]">{t}</span>
                      </div>
                    ))}
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-sm border border-[#1a472a] bg-[#D5D1CC] relative overflow-hidden">
                        <div className="absolute inset-0 opacity-30" style={{
                          background: 'repeating-linear-gradient(45deg, #888 0, #888 1px, transparent 0, transparent 4px)',
                        }} />
                      </div>
                      <span className="text-[10px] text-[#5E5A56]">Border Row</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-[#E8E6E1]">
                <button onClick={exportPDF} disabled={exporting}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50"
                  style={{ backgroundColor: '#1a472a' }}
                  data-testid="export-layout-pdf">
                  {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  Download PDF
                </button>
                <button onClick={() => { setLayout(null); }}
                  className="px-5 py-2.5 rounded-xl text-sm font-semibold text-[#5E5A56] border border-[#E8E6E1] hover:bg-[#f5f5f5] transition"
                  data-testid="regenerate-layout-btn">
                  Re-randomize
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
